import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges,} from '@angular/core';
import {FieldTable, FilterConf, SelectItem} from '@tj/tj-dmp-lib-api';
import {TranslateService} from '@ngx-translate/core';
import {AbstractFilterComponent} from '../abstract-filter.component';

const EQUALITY_OPERATORS = ['EQUAL', 'NOT_EQUAL'];

const DATE_TYPES = new Set<FieldTable.TypeEnum>(['Date', 'DateTime', 'Time']);
const NUMBER_TYPES = new Set<FieldTable.TypeEnum>(['Int', 'BigInt', 'Decimal_5_2', 'Decimal_8_2', 'Decimal_11_2']);

// Séparateur décimal, aligné sur field.localInput ('EN'/'FR') utilisé côté mapping (ConvertTypeTransformConf).
const DECIMAL_LOCALES: { [key: string]: string } = {EN: 'en-US', FR: 'fr-FR'};

@Component({
    selector: 'dmp-filter-etl',
    templateUrl: './filter-etl.component.html',
    styleUrls: ['./filter-etl.component.scss'],
    standalone: false
})
export class FilterEtlComponent extends AbstractFilterComponent implements OnInit, OnChanges {
    @Input() filter: FilterConf = null;
    /** Type SQL du champ destination par code de colonne source. Absent = pas encore mappée. */
    @Input() fieldTypeByCode: { [code: string]: FieldTable.TypeEnum } = {};
    @Output() filterChange = new EventEmitter<FilterConf>();

    // Suffixe unique pour les id des inputs (plusieurs lignes de filtre sur la même page).
    private static instanceCounter = 0;
    readonly uid = `filter-etl-${FilterEtlComponent.instanceCounter++}`;

    private nonDateOperators: SelectItem[];
    private dateOnlyOperators: SelectItem[];
    private dateOperators: SelectItem[];
    private numberOperators: SelectItem[];
    private numberComparisonOperators: string[];

    operators: SelectItem[];
    operator: string = null;
    patternsDate: SelectItem[];
    decimalSeparatorOptions: SelectItem[];

    constructor(private translate: TranslateService) {
        super();
        this.nonDateOperators = [
            {label: this.translate.instant('etl.new.design.filter-empty'), value: 'IS_EMPTY'},
            {label: this.translate.instant('etl.new.design.filter-equal'), value: 'EQUAL'},
            {label: this.translate.instant('etl.new.design.filter-not-equal'), value: 'NOT_EQUAL'},
        ];
        this.dateOnlyOperators = [
            {label: this.translate.instant('etl.new.design.filter-less-equal-date'), value: 'LESS_THAN_OR_EQUAL'},
            {label: this.translate.instant('etl.new.design.filter-less-equal-date-delta'), value: 'LESS_THAN_OR_EQUAL_DELTA'},
            {label: this.translate.instant('etl.new.design.filter-greater-equal-date'), value: 'GREATER_THAN_OR_EQUAL'},
            {label: this.translate.instant('etl.new.design.filter-greater-equal-date-delta'), value: 'GREATER_THAN_OR_EQUAL_DELTA'},
            // pas de use case
            // {label: this.translate.instant('etl.new.design.filter-include'), value: 'INCLUDE'},
        ];
        // "Égal à" y vaut EQUAL_DATE (comparaison de dates parsées), pas EQUAL (comparaison de chaîne brute).
        this.dateOperators = [
            {label: this.translate.instant('etl.new.design.filter-empty'), value: 'IS_EMPTY'},
            {label: this.translate.instant('etl.new.design.filter-equal'), value: 'EQUAL_DATE'},
            ...this.dateOnlyOperators,
        ];
        // "between" s'obtient en ajoutant deux filtres (un >= et un <=) sur le même champ : ils s'appliquent
        // tous les deux côté dmp-engine (indexMap dans getActionsImport), pas besoin d'un opérateur dédié.
        this.numberOperators = [
            ...this.nonDateOperators,
            {label: this.translate.instant('etl.new.design.filter-greater-than'), value: 'NUMBER_GREATER_THAN'},
            {label: this.translate.instant('etl.new.design.filter-greater-equal'), value: 'NUMBER_GREATER_THAN_OR_EQUAL'},
            {label: this.translate.instant('etl.new.design.filter-less-than'), value: 'NUMBER_LESS_THAN'},
            {label: this.translate.instant('etl.new.design.filter-less-equal'), value: 'NUMBER_LESS_THAN_OR_EQUAL'},
        ];
        this.numberComparisonOperators = this.numberOperators.slice(this.nonDateOperators.length).map(o => o.value);

        this.operators = [...this.nonDateOperators, ...this.dateOnlyOperators];
        this.operator = this.nonDateOperators[0].value;
        // Mêmes libellés que le choix EN/FR côté mapping (etl-popup-design-param).
        this.decimalSeparatorOptions = [
            {label: this.translate.instant('etl.new.design.nombreFR'), value: 'FR'},
            {label: this.translate.instant('etl.new.design.nombreEN'), value: 'EN'},
        ];
        this.patternsDate = [];
        ['yyyy-MM-dd', 'dd/MM/yyyy HH:mm', 'HH:mm'].forEach(p => {
            this.patternsDate.push({
                label: p,
                value: p
            });
        });
    }

    ngOnInit() {
        if (this.filter && this.filter.field) {
            this.itemCol = this.filter.field;
        }
        this.updateListCol();
        this.updateOperatorsForField(this.itemCol);
        this.applyNumberDefaults(this.itemCol);
        this.normalizeDateValueForDisplay();
    }

    // p-datepicker attend un objet Date ; en édition la valeur vient de l'API en chaîne ISO, d'où la conversion.
    private normalizeDateValueForDisplay() {
        if (this.filter && this.isDate(this.filter.operator) && typeof this.filter.value === 'string' && this.filter.value) {
            (this.filter as unknown as { value: Date }).value = new Date(this.filter.value);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.cols) {
            this.updateListCol();
        }
        if (changes.fieldTypeByCode) {
            // fieldTypeByCode arrive de manière asynchrone (appel HTTP dans le parent) : au premier rendu
            // il est encore vide, d'où un recalcul ici une fois les types réellement connus.
            this.updateOperatorsForField(this.itemCol);
            this.applyNumberDefaults(this.itemCol);
        }
    }

    // Type inconnu (champ pas encore mappé) : tous les opérateurs restent proposés par défaut.
    private updateOperatorsForField(field: string) {
        const type = field ? this.fieldTypeByCode?.[field] : null;
        if (type && DATE_TYPES.has(type)) {
            this.operators = this.dateOperators;
        } else if (type && NUMBER_TYPES.has(type)) {
            this.operators = this.numberOperators;
        } else if (type) {
            this.operators = this.nonDateOperators;
        } else {
            this.operators = [...this.nonDateOperators, ...this.dateOnlyOperators];
        }
        if (this.filter && !this.operators.some(o => o.value === this.filter.operator)) {
            this.filter.operator = this.operators[0].value as FilterConf.OperatorEnum;
            this.update();
        }
    }

    isNumberComparison(operator): boolean {
        return operator && this.numberComparisonOperators.includes(operator);
    }

    isEqualityOperator(operator): boolean {
        return operator && EQUALITY_OPERATORS.includes(operator);
    }

    private currentType(): FieldTable.TypeEnum {
        return this.filter?.field ? this.fieldTypeByCode?.[this.filter.field] : null;
    }

    get isNumberCategory(): boolean {
        return NUMBER_TYPES.has(this.currentType());
    }

    // filter.decimal / filter.localInput sont persistés (FilterConf) : la case "Décimal" et le séparateur
    // lisent/écrivent directement dessus, pour ne pas se faire écraser par le mapping à chaque rechargement.
    get isDecimalInput(): boolean {
        return !!this.filter?.decimal;
    }

    set isDecimalInput(value: boolean) {
        if (this.filter) {
            this.filter.decimal = value;
        }
    }

    get decimalSeparator(): 'EN' | 'FR' {
        return this.filter?.localInput ?? 'FR';
    }

    set decimalSeparator(value: 'EN' | 'FR') {
        if (this.filter) {
            this.filter.localInput = value;
        }
    }

    decimalPlaces = 2;
    unlimitedDecimals = false;

    // filter.decimal / filter.localInput absents : nouveau filtre, ou filtre enregistré avant l'ajout de
    // ces champs. On les initialise alors depuis la valeur déjà stockée si possible (elle reflète ce qui
    // a réellement été enregistré), sinon depuis le type de mapping (meilleure estimation disponible).
    private applyNumberDefaults(field: string) {
        if (!this.filter) {
            return;
        }
        const type = field ? this.fieldTypeByCode?.[field] : null;
        const isDecimalType = !!type && type.startsWith('Decimal');
        const mappingScale = isDecimalType ? (Number(type.split('_').pop()) || 2) : 2;

        const rawValue = this.filter.value;
        const decimalMatch = rawValue != null ? /[.,](\d+)$/.exec(String(rawValue)) : null;

        if (this.filter.decimal == null) {
            if (decimalMatch) {
                this.filter.decimal = true;
            } else if (rawValue != null && rawValue !== '') {
                // Valeur déjà enregistrée, sans partie décimale : l'utilisateur avait choisi "entier".
                this.filter.decimal = false;
            } else {
                this.filter.decimal = isDecimalType;
            }
        }
        // Le nombre de décimales suit toujours la valeur déjà stockée quand il y en a une.
        this.decimalPlaces = decimalMatch ? decimalMatch[1].length : mappingScale;
        if (this.filter.localInput == null) {
            this.filter.localInput = 'FR';
        }
        this.unlimitedDecimals = false;
    }

    get numberMaxFractionDigits(): number {
        if (!this.isDecimalInput) {
            return 0;
        }
        return this.unlimitedDecimals ? 20 : this.decimalPlaces;
    }

    get numberLocale(): string {
        return DECIMAL_LOCALES[this.decimalSeparator];
    }

    // Dérivé du pattern choisi par l'utilisateur (pas du mapping) : le mapping ne dit rien sur la forme
    // réelle de la donnée source, qui peut différer (transformation, colonne source mappée plusieurs fois...).
    get showTimePicker(): boolean {
        return /[Hhms]/.test(this.filter?.pattern || '');
    }

    get timeOnlyPicker(): boolean {
        const pattern = this.filter?.pattern || '';
        return /[Hhms]/.test(pattern) && !/[yMdD]/.test(pattern);
    }

    removeValue(index: number) {
        this.filter.values.splice(index, 1);
        this.update();
    }

    addValue(event = null) {
        console.log(event);
        this.filter.values.push('');
        this.update();
    }

    update() {
        this.filterChange.emit(this.filter);
        if (['GREATER_THAN_OR_EQUAL_DELTA', 'LESS_THAN_OR_EQUAL_DELTA'].includes(this.filter.operator)) {
            this.filter.value = '365';
        } else {
            this.filter.value = null;
        }
        // Le pattern (format de la colonne source) n'a de sens que pour les opérateurs date : sinon il
        // reste un résidu de la config précédente du filtre (ex. alterné vers un champ NUMBER).
        if (!this.isDateOrDeltaDate(this.filter.operator)) {
            this.filter.pattern = null;
        }
    }

    updateFilter(field: string) {
        this.updateExampleData(field);
        // Ne doit pas survivre à un changement de colonne, comme le pattern (cf. update()).
        this.filter.decimal = null;
        this.filter.localInput = null;
        this.updateOperatorsForField(field);
        this.applyNumberDefaults(field);
    }

    isDate(operator) {
        return operator && ['LESS_THAN_OR_EQUAL', 'GREATER_THAN_OR_EQUAL', 'EQUAL_DATE'].includes(operator);
    }

    isDateOrDeltaDate(operator) {
        return operator && ['LESS_THAN_OR_EQUAL', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN_OR_EQUAL_DELTA', 'GREATER_THAN_OR_EQUAL_DELTA', 'EQUAL_DATE'].includes(operator);
    }

    isDeltaDate(operator) {
        return operator && ['LESS_THAN_OR_EQUAL_DELTA', 'GREATER_THAN_OR_EQUAL_DELTA'].includes(operator);
    }
}
